import { useEffect, useState } from 'react';
import { Transition } from '@headlessui/react';
import toast from 'react-hot-toast';

import {
  InvoiceOfflinePaymentMethod,
  ProgramInvoiceDataFragment,
  useMarkInvoicePaidMutation,
  useProgramInvoicesForProgramQuery,
  useSendInvoiceReminderMutation,
  useVoidInvoiceMutation,
} from '../../../../generated/graphql';

import Spinner from '../../../svgs/Spinner';
import { defaultTransitionProps } from '../../../lib/animation';
import ToastAlert from '../../../components/ToastAlert';
import { getGraphQLErrorMessage } from '../../../../lib/errors';

import { usePatientProfileContext } from '..';

import EmptyProfileTab from '../../../components/EmptyProfileTab';
import { useAuth } from '../../../../contexts/AuthContext';
import IllustrationBanknotes from '../../../svgs/IllustrationBanknotes';
import InvoiceTable from '../../Payments/Table';
import { ColumnOrder } from '../../../types/tables';
import { sortInvoices, sortOptions } from '../../Payments/helpers';
import useOpenProgramInvoice from '../../Payments/hooks/useOpenProgramInvoice';
import MarkInvoicePaidModal from '../../Payments/MarkInvoicePaidModal';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/Button';
import { PlusSmIcon } from '@heroicons/react/outline';

const Invoices = () => {
  const navigate = useNavigate();
  const { patient } = usePatientProfileContext();

  const { authedProviderUser } = useAuth();
  const hasStripeIntegrationSetup =
    authedProviderUser?.provider.stripeAccountId;

  const {
    data: programInvoicesData,
    loading: programInvoicesLoading,
    error: programInvoicesError,
    refetch: refetchProgramInvoices,
  } = useProgramInvoicesForProgramQuery({
    variables: {
      programId: patient?.programInstanceId,
    },
    skip: !hasStripeIntegrationSetup || !patient?.programInstanceId,
  });

  useEffect(() => {
    if (programInvoicesError) {
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message="Unable to fetch invoices."
          level="error"
        />
      ));
    }
  }, [programInvoicesError]);

  const [sendInvoiceReminder] = useSendInvoiceReminderMutation();
  const [voidInvoice] = useVoidInvoiceMutation();
  const [markInvoicePaid] = useMarkInvoicePaidMutation();
  const {
    actionLoadingInvoiceId,
    setActionLoadingInvoiceId,
    openProgramInvoice,
  } = useOpenProgramInvoice({ sourcePage: 'patient_profile' });

  const [invoiceOrder, setInvoiceOrder] = useState<ColumnOrder>(
    sortOptions.newestCreated,
  );
  const [invoiceToMarkPaid, setInvoiceToMarkPaid] =
    useState<ProgramInvoiceDataFragment | null>(null);
  const [isMarkPaidSubmitting, setIsMarkPaidSubmitting] = useState(false);

  const allProgramInvoices = programInvoicesData?.programInvoices || [];

  const sortedProgramInvoices = sortInvoices(allProgramInvoices, invoiceOrder);

  const handleViewInvoice = (programInvoice: ProgramInvoiceDataFragment) =>
    openProgramInvoice(programInvoice, 'view');

  const handleDownloadInvoice = (programInvoice: ProgramInvoiceDataFragment) =>
    openProgramInvoice(programInvoice, 'download');

  const handleSendInvoiceReminder = async (
    programInvoice: ProgramInvoiceDataFragment,
  ) => {
    try {
      const { data } = await sendInvoiceReminder({
        variables: {
          programInvoiceId: programInvoice.id,
        },
      });
      if (data?.sendInvoiceReminder) {
        toast.custom(({ visible }) => (
          <ToastAlert
            isVisible={visible}
            level="success"
            message={`Successfully sent an invoice reminder to ${programInvoice.patient?.name}`}
          />
        ));
      }
    } catch (err) {
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message="Something went wrong."
          level="error"
        />
      ));
    }
  };

  const handleVoidInvoice = async (
    programInvoice: ProgramInvoiceDataFragment,
  ) => {
    try {
      setActionLoadingInvoiceId(programInvoice.id);
      const { data } = await voidInvoice({
        variables: {
          programInvoiceId: programInvoice.id,
        },
      });
      if (data?.voidInvoice) {
        refetchProgramInvoices();
        toast.custom(({ visible }) => (
          <ToastAlert
            isVisible={visible}
            level="success"
            message={`Successfully voided invoice for ${programInvoice.patient?.name}`}
          />
        ));
      }
    } catch (err) {
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message={getGraphQLErrorMessage(err)}
          level="error"
        />
      ));
    } finally {
      setActionLoadingInvoiceId(null);
    }
  };

  const handleMarkInvoicePaid = (
    programInvoice: ProgramInvoiceDataFragment,
  ) => {
    setInvoiceToMarkPaid(programInvoice);
  };

  const handleConfirmMarkInvoicePaid = async ({
    paymentMethod,
    note,
  }: {
    paymentMethod: InvoiceOfflinePaymentMethod;
    note?: string;
  }) => {
    if (!invoiceToMarkPaid) {
      return;
    }
    try {
      setIsMarkPaidSubmitting(true);
      setActionLoadingInvoiceId(invoiceToMarkPaid.id);
      const { data } = await markInvoicePaid({
        variables: {
          input: {
            programInvoiceId: invoiceToMarkPaid.id,
            paymentMethod,
            note,
          },
        },
      });
      if (data?.markInvoicePaid) {
        refetchProgramInvoices();
        setInvoiceToMarkPaid(null);
        toast.custom(({ visible }) => (
          <ToastAlert
            isVisible={visible}
            level="success"
            message={`Marked invoice as paid for ${invoiceToMarkPaid.patient?.name}`}
          />
        ));
      }
    } catch (err) {
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message={getGraphQLErrorMessage(err)}
          level="error"
        />
      ));
    } finally {
      setIsMarkPaidSubmitting(false);
      setActionLoadingInvoiceId(null);
    }
  };

  const navigateToSendInvoice = () => {
    navigate(`/payments/new`, {
      state: { programId: patient?.programInstanceId },
    });
  };

  const clientName = patient?.firstName || 'this client';

  const showInvoicesContent =
    hasStripeIntegrationSetup &&
    !programInvoicesError &&
    !programInvoicesLoading;

  const emptySubTitle = `Invoices sent to ${clientName} through Homecoming will show up here.`;

  return (
    <>
      <Transition
        show={showInvoicesContent}
        {...defaultTransitionProps}
        className="w-full pt-5"
      >
        <div className="flex flex-col items-start justify-start">
          <div className="mb-3 flex w-full flex-row items-center justify-between">
            <Button
              title="Send invoice"
              size="small"
              iconPosition="left"
              IconComponent={PlusSmIcon}
              onClick={navigateToSendInvoice}
            />
          </div>
          {allProgramInvoices.length ? (
            <InvoiceTable
              hasAnyProgramInvoices={Boolean(allProgramInvoices.length)}
              programInvoices={sortedProgramInvoices}
              programInvoicesLoading={programInvoicesLoading}
              onClickSendReminder={handleSendInvoiceReminder}
              onClickMarkInvoicePaid={handleMarkInvoicePaid}
              onClickVoidInvoice={handleVoidInvoice}
              onClickViewInvoice={handleViewInvoice}
              onClickDownloadInvoice={handleDownloadInvoice}
              invoiceOrder={invoiceOrder}
              setInvoiceOrder={setInvoiceOrder}
              actionLoadingInvoiceId={actionLoadingInvoiceId}
            />
          ) : (
            <EmptyProfileTab
              IconComponent={IllustrationBanknotes}
              iconClassName={'text-secondary-125'}
              titleText={`${patient?.firstName || 'Patient'}'s invoices`}
              subTitleText={emptySubTitle}
              onClickButton={navigateToSendInvoice}
              buttonTitle={'Send an invoice'}
            />
          )}
        </div>
      </Transition>

      {programInvoicesLoading && <Spinner className="mx-auto mt-8" />}
      <MarkInvoicePaidModal
        isOpen={Boolean(invoiceToMarkPaid)}
        invoice={invoiceToMarkPaid}
        isSubmitting={isMarkPaidSubmitting}
        onClose={() => setInvoiceToMarkPaid(null)}
        onConfirm={handleConfirmMarkInvoicePaid}
      />
    </>
  );
};

export default Invoices;
